/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX } from 'react';
import React from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';

import { updateField } from '../../store/customer';

interface FieldCheckboxProps {
  formik: any;
  name: string;
  label: string;
  dispatch: (action: any) => void;
  store: any;
  apiService: any;
  onChecked?: (args: { formik: any; dispatch: (action: any) => void; apiService: any; store: any }) => void;
  onCheckboxClicked?: (formik: any) => void;
  alignSelf?: string;
}

function FieldCheckbox({
  formik,
  name,
  label,
  dispatch,
  store,
  apiService,
  onChecked = () => null,
  onCheckboxClicked = () => null,
  alignSelf
}: FieldCheckboxProps): JSX.Element {
  // Reject keys that could pollute Object.prototype
  const isUnsafeKey = (key: string | number) => key === '__proto__' || key === 'constructor' || key === 'prototype';

  // Tokenize a path supporting dot and bracket notation
  const tokenizePath = (path: string) => {
    const tokens: Array<string | number> = [];
    const re = /([^[.\]]+)|\[(\d+)\]/g;
    let m: RegExpExecArray | null;

    while ((m = re.exec(String(path))) != null) {
      const key = m[1] != null ? m[1] : m[2];
      tokens.push(m[2] != null ? Number(key) : key);
    }

    return tokens;
  };

  // Initialize root container based on path type
  const initializeRootContainer = (obj: any, isArrayPath: boolean) => {
    if (isArrayPath) {
      return Array.isArray(obj) ? [...obj] : [];
    }
    if (Array.isArray(obj)) {
      return [...obj];
    }
    return { ...obj };
  };

  // Create next container based on next key type
  const createNextContainer = (existing: any, shouldBeArray: boolean) => {
    if (shouldBeArray) {
      return Array.isArray(existing) ? [...existing] : [];
    }
    if (typeof existing === 'object' && existing != null && !Array.isArray(existing)) {
      return { ...existing };
    }
    return {};
  };

  // Set a nested value by dot/bracket path without mutating original
  const setByPath = (obj: any, path: string | Array<string | number>, value: unknown) => {
    const keys = Array.isArray(path) ? path : tokenizePath(path);
    if (!keys.length) {
      return Array.isArray(obj) ? [...obj] : { ...obj };
    }

    // Guard against prototype pollution via crafted paths
    if (keys.some(isUnsafeKey)) {
      return Array.isArray(obj) ? [...obj] : { ...obj };
    }

    const isArrayPath = typeof keys[0] === 'number';
    const result = initializeRootContainer(obj, isArrayPath);
    let cur = result;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const nextKey = keys[i + 1];
      const shouldBeArray = typeof nextKey === 'number';
      const nextContainer = createNextContainer(cur[key], shouldBeArray);

      cur[key] = nextContainer;
      cur = cur[key];
    }

    const leafKey = keys[keys.length - 1];
    cur[leafKey] = value;

    return result;
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    // Update Redux + Formik
    dispatch(updateField({ name, value: checked }));
    formik.form.setFieldValue(name, checked);

    // Build API payload
    const updatedCustomer = setByPath(store.customer, name, checked);

    apiService.updateCustomer(updatedCustomer, dispatch);

    if (checked) {
      onChecked({ formik, dispatch, apiService, store });
    }

    onCheckboxClicked(formik);
  };

  const checkboxControl = (
    <Checkbox
      name={name}
      checked={formik.field.value || false}
      onChange={handleOnChange}
      slotProps={{
        input: {
          'aria-describedby': `${name}-helper-text`
        }
      }}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        alignSelf: alignSelf || 'flex-start',
        marginBottom: name && !name.includes('taxExempt') ? '16px' : 0,
        marginTop: 0
      }}
    >
      <FormControl component="fieldset" variant="standard" className="sprout-checkbox">
        <FormGroup>
          <FormControlLabel
            control={checkboxControl}
            label={label}
            sx={{
              '& .MuiFormControlLabel-label': {
                color: 'var(--Text-text-primary, #101C16)',
                font: 'var(--sprout-font-body1)'
              }
            }}
          />
        </FormGroup>
      </FormControl>
    </Box>
  );
}

export default FieldCheckbox;
